import type { PlayApi, PlaySession } from '@mogzo/play';
import type { Genre, Listing, ListingImage } from '@mogzo/shared';
import { API_URL, apiFetch } from './api';

/** A live game (M23), as `/v1/games/<address>` answers its developer and MOGZO staff. */
export interface LiveGame {
  game: { id: string; name: string; slug: string };
  version: string;
  buildNumber: number;
  playUrl: string;
  listing: Listing;
  images: ListingImage[];
  /** Null for someone signed out. */
  viewer: { isDeveloper: boolean; isStaff: boolean; joined: boolean } | null;
  /** Its public community (M29): the game page's Posts tab. */
  community: { id: string; memberCount: number } | null;
  suspended: boolean;
}

/** A live game as Discover, the feed and the library show it (M29). */
export interface GameCard {
  id: string;
  name: string;
  slug: string;
  path: string;
  genre: Genre | null;
  developer: string;
  shortDescription: string;
  iconUrl: string | null;
  coverUrl: string | null;
  liveSince: string;
  isNew: boolean;
}

export interface DiscoverData {
  games: GameCard[];
  /** Ids of the games the viewer has played. */
  played: string[];
  alike: { name: string; games: GameCard[] } | null;
}

export interface HomeData {
  recent: GameCard[];
  suggested: GameCard[];
}

export interface LibraryGame extends GameCard {
  lastPlayedAt: string | null;
  joined: boolean;
}

export const fetchDiscover = () => apiFetch<DiscoverData>('/v1/discover');
export const fetchHome = () => apiFetch<HomeData>('/v1/home');
export const fetchMyGames = () => apiFetch<{ games: LibraryGame[] }>('/v1/me/games');
/** "More games" in a game's play page side panel: its genre first, then the newest. */
export const fetchMoreGames = (slug: string) =>
  apiFetch<{ games: GameCard[] }>(`/v1/games/${encodeURIComponent(slug)}/more`);

/** Joins a public community: a game's, from its game page. */
export function joinCommunity(communityId: string) {
  return apiFetch<{ role: string; memberCount: number }>(`/v1/communities/${communityId}/join`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function fetchLiveGame(slug: string): Promise<LiveGame> {
  return apiFetch<LiveGame>(`/v1/games/${encodeURIComponent(slug)}`);
}

/** Play sessions in the live game's own data, not its test community's. */
export function livePlayApi(slug: string): PlayApi {
  return {
    apiUrl: API_URL,
    startSession: () =>
      apiFetch<PlaySession>(`/v1/games/${encodeURIComponent(slug)}/play-session`, {
        method: 'POST',
      }),
  };
}
